/* eslint-disable indent */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import './index.scss';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import { Dropdown, Menu, message } from 'antd';

import { RootState } from '@/redux/store';
import { CockpitMode, CockpitStatus, CockpitStatusText } from '@/utils/type';
import SettingBoard from '@/components/SettingBoard';
import CollectTaskModal from '@/components/CollectTaskModal';
import { passportExit } from '@/views/Login/utils';
import { getClstag, getPermissionById } from '@/utils/util';
import { PermissionButton } from '@/utils/constant';
import { HttpStatusCode } from '@/fetch/core/constant';
import { actionExitCockpit } from '@/redux/reducers/newCommon';
import CockpitManageApi from '@/fetch/bussiness/cockpitManage';
import {
  addGlobalEventListener,
  removeGlobalEventListener,
} from '@/utils/emit';

const Header: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const fetchApi = useRef(new CockpitManageApi()).current;

  // 状态与标识
  const [taskListModal, setTaskListModal] = useState<boolean>(false);
  const [enterBoard, setEnterBoard] = useState<boolean>(false);
  const [showSettingBoard, setShowSettingBoard] = useState<boolean>(false);

  // Redux 数据
  const login = useSelector((state: RootState) => state.login);
  const newCommon = useSelector((state: RootState) => state.newCommon);
  const { cockpitNumber, cockpitStatus, cockpitMode } = newCommon;

  // 权限与显示逻辑
  const onlineStatus = [CockpitStatus.WORK, CockpitStatus.REST].includes(
    cockpitStatus
  );
  const cockpitmanagePermission = getPermissionById(
    PermissionButton.SPV_COCKPIT_MANAGE
  );

  const contextRef = useRef({
    isMounted: false,
    cockpitStatus: cockpitStatus,
    navigate,
    dispatch,
  });

  // 同步状态到 Ref
  useEffect(() => {
    contextRef.current.isMounted = true;
    contextRef.current.cockpitStatus = cockpitStatus;
    return () => {
      contextRef.current.isMounted = false;
    };
  }, [cockpitStatus]);

  // 退出登录逻辑
  const handleLogout = useCallback(async () => {
    const { cockpitStatus: currentStatus } = contextRef.current;

    const performExit = () => {
      passportExit();
      localStorage.setItem('logout', 'logout');
    };

    if ([CockpitStatus.WORK, CockpitStatus.REST].includes(currentStatus)) {
      try {
        const res = await fetchApi.quitCockpit();
        if (res.code === HttpStatusCode.Success) {
          performExit();
        } else {
          message.error(res.message);
        }
      } catch (err) {
        performExit();
      }
    } else {
      performExit();
    }
  }, [fetchApi]);

  // 退出座席逻辑
  const quitCockpit = useCallback(async () => {
    try {
      const res = await fetchApi.quitCockpit();
      // 检查挂载状态，防止操作已销毁的组件状态
      if (!contextRef.current.isMounted) return;

      if (res.code === HttpStatusCode.Success) {
        message.success('退出座席成功');
        contextRef.current.navigate('/app/home');
        contextRef.current.dispatch(actionExitCockpit());
        setEnterBoard(false);
      } else {
        message.error(res.message);
      }
    } catch (err) {
      console.error('quitCockpit error:', err);
    }
  }, [fetchApi]);

  // 重置状态回调
  const handleResetStatus = useCallback(() => {
    if (contextRef.current.isMounted) setEnterBoard(false);
  }, []);

  // 点击外部关闭设置面板
  const handleDocumentClick = useCallback((e: MouseEvent) => {
    // 动态查询，不持久化存储节点引用
    const settingBoardNode = document.querySelector('.cockpit-setting-board');
    const btnNode = document.querySelector('.status-info');

    if (!settingBoardNode) return;

    const target = e.target as Node;
    const isInsideBoard = settingBoardNode.contains(target);
    const isInsideBtn = btnNode?.contains(target);

    if (!isInsideBoard && !isInsideBtn) {
      setShowSettingBoard(false);
    }
  }, []);

  // --- 生命周期：严格管控事件监听 ---
  useEffect(() => {
    // 注册全局业务事件
    addGlobalEventListener('LOGIN_OUT', handleLogout);
    addGlobalEventListener('QUIT_COCKPIT', quitCockpit);
    addGlobalEventListener('RESET_STATUS', handleResetStatus);

    // 注册 DOM 事件
    document.addEventListener('click', handleDocumentClick);

    return () => {
      // 彻底移除所有监听，防止闭包锁死 DOM
      removeGlobalEventListener('LOGIN_OUT', handleLogout);
      removeGlobalEventListener('QUIT_COCKPIT', quitCockpit);
      removeGlobalEventListener('RESET_STATUS', handleResetStatus);
      document.removeEventListener('click', handleDocumentClick);
    };
  }, [handleLogout, quitCockpit, handleResetStatus, handleDocumentClick]);

  // --- 交互逻辑 ---
  const toCockpitBoardView = () => {
    setEnterBoard(true);
    navigate('/app/cockpitmanage');
  };

  const exitCockpitBoardView = () => {
    setEnterBoard(false);
    navigate('/app/multivehicle');
  };

  // 用户下拉菜单渲染
  const menu = (
    <Menu>
      <Menu.Item className="logout" key="logout" onClick={handleLogout}>
        退出登录
      </Menu.Item>
    </Menu>
  );

  return (
    <div className="header">
      <div className="left">
        <i className="app-logo"></i>
      </div>

      <div className="right">
        {onlineStatus &&
          cockpitmanagePermission &&
          cockpitMode !== CockpitMode.PATROL_MODE && (
            <>
              {!enterBoard ? (
                <i
                  className="cockpit-board"
                  onClick={toCockpitBoardView}
                  clstag={getClstag(cockpitMode, 'MultiPage_CockpitManage')}
                >
                  座席管理
                </i>
              ) : (
                <i className="cockpit-exit" onClick={exitCockpitBoardView}>
                  退出座席管理
                </i>
              )}
              {cockpitMode === CockpitMode.MAP_COLLECTION_MODE && (
                <i
                  className="map-task-list"
                  onClick={() => setTaskListModal(true)}
                >
                  任务列表
                </i>
              )}
              <i className="line"></i>
            </>
          )}

        {cockpitMode === CockpitMode.FREE_MODE ? (
          <div className="exit-btn" onClick={quitCockpit}>
            退出座席
          </div>
        ) : (
          <div
            className="status-info"
            onClick={() =>
              cockpitMode && setShowSettingBoard(!showSettingBoard)
            }
          >
            <i className="status-icon"></i>
            <i
              className={classNames('cockpit-name', {
                disabeld: !cockpitMode,
              })}
              clstag={getClstag(cockpitMode, 'MultiPage_CockpitNumber')}
            >
              {cockpitNumber}
            </i>
            <i
              className={classNames('working-status', {
                working: cockpitStatus === CockpitStatus.WORK,
                resting: cockpitStatus === CockpitStatus.REST,
                offline: cockpitStatus === CockpitStatus.OFFLINE,
              })}
            >
              {CockpitStatusText[cockpitStatus as keyof typeof CockpitStatus]}
            </i>
          </div>
        )}

        <Dropdown
          overlay={menu}
          trigger={['click']}
          arrow
          placement="bottomRight"
        >
          <div className="user-info">
            <img
              src={require('@/assets/images/header/ico.png')}
              className="user-avatar"
              alt="avatar"
            />
            <i>{login?.userInfo?.userName}</i>
          </div>
        </Dropdown>
      </div>

      {showSettingBoard && (
        <SettingBoard handleClose={() => setShowSettingBoard(false)} />
      )}

      <CollectTaskModal
        visible={taskListModal}
        setVisible={setTaskListModal}
        cockpitNumber={cockpitNumber}
      />
    </div>
  );
};

export default Header;
